import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { IDataCardFE } from '../../../../../models/data-card.model'
import { HelperService } from '../../../../../services/helper.service'
import { HttpErrorResponse } from '@angular/common/http'
import {
  DataCardGroupType,
  DataCardService,
  DataCardType,
} from '../../../../../services/data-card.service'
import { HttpResponseHandlerService } from '../../../../../services/http-response-handler.service'
import { TypicalFuncLabelEnum } from '../../../../../enums/typical-func-label.enum'
import { TypicalEquipmentLabelEnum } from '../../../../../enums/typical-equipment-label.enum'
import { RegexEnum } from '../../../../../enums/regex.enum'

@Component({
    selector: 'app-form-main',
    templateUrl: './form-main.component.html',
    styleUrls: ['./form-main.component.scss'],
    standalone: false
})
export class FormMainComponent implements OnInit {
  @Input() mainObject: string
  @Input() dataCardType: DataCardType

  @Input() set mainFormData(formData: IDataCardFE) {
    this._mainFormData = formData
    setTimeout((): void => {
      this.patchMainFormData()
    }, 100)
  }

  @Input() set urlFormData(formData: { lastChangedBy: string; lastChanged: string; url: string }) {
    this._urlFormData = formData
    setTimeout((): void => {
      this.patchFormUrlData()
    }, 100)
  }

  @Input() displayMainForm: boolean = false
  @Input() disabledMainFormControls: boolean = false
  @Input() displayUrlForm: boolean = false

  @Output() notifyDataCard: EventEmitter<any> = new EventEmitter()
  @Output() notifyUrlDataCard: EventEmitter<any> = new EventEmitter()

  private _mainFormData: IDataCardFE
  private _urlFormData: { lastChangedBy: string; lastChanged: string; url: string }

  public get mainFormData(): IDataCardFE {
    return this._mainFormData
  }

  public get urlFormData(): { lastChangedBy: string; lastChanged: string; url: string } {
    return this._urlFormData
  }

  public form: FormGroup<{
    condition: FormControl<string | null>
    comment: FormControl<string | null>
    valve: FormControl<string | null>
    sol1: FormControl<string | null>
    sol2: FormControl<string | null>
    url: FormControl<string | null>
  }>
  public condition: FormControl<string | null>
  public comment: FormControl<string | null>
  public valve: FormControl<string | null>
  public sol1: FormControl<string | null>
  public sol2: FormControl<string | null>
  public url: FormControl<string | null>

  private _isLoadingMainControls: boolean = false
  private _isLoadingUrlControls: boolean = false

  private _initialMainForm: any = {}
  private _initialUrlForm: any = {}

  constructor(
    private service: DataCardService,
    private handlerResponseService: HttpResponseHandlerService,
  ) {}

  private initFormControls(): void {
    this.condition = new FormControl<string | null>({
      value: null,
      disabled: this.disabledMainFormControls,
    })
    this.comment = new FormControl<string | null>({
      value: null,
      disabled: this.disabledMainFormControls,
    })
    this.valve = new FormControl<string | null>(
      { value: null, disabled: this.disabledMainFormControls },
      [Validators.maxLength(50)],
    )
    this.sol1 = new FormControl<string | null>(
      { value: null, disabled: this.disabledMainFormControls },
      [Validators.maxLength(50)],
    )
    this.sol2 = new FormControl<string | null>(
      { value: null, disabled: this.disabledMainFormControls },
      [Validators.maxLength(50)],
    )
    this.url = new FormControl<string | null>(null, [
      Validators.maxLength(100),
      Validators.pattern(RegexEnum.REGEX_URL),
    ])
  }

  private initFormGroup(): void {
    this.form = new FormGroup({
      condition: this.condition,
      comment: this.comment,
      valve: this.valve,
      sol1: this.sol1,
      sol2: this.sol2,
      url: this.url,
    })
  }
  public onSaveDataCard(): void {
    if (this.form.invalid) {
      return
    }

    if (this.displayMainForm && !this.disabledMainFormControls && this.mainFormChanged()) {
      this.saveMainForm()
    }

    if (this.displayUrlForm && this.urlFormChanged()) {
      this.saveUrlForm()
    }
  }

  private saveMainForm(): void {
    this._isLoadingMainControls = true
    this.service.updateEnrich(this.mainObject, this.getMainFormValues()).subscribe({
      next: (resource?: IDataCardFE): void => {
        this.notifyDataCard.next({
          mainObject: this.mainObject,
          condition: this.condition.value,
          comment: this.comment.value,
        })
        this._initialMainForm = this.getMainFormValues()
        this._isLoadingMainControls = false
        this.handlerResponseService.notifySuccess('Success', 'Data updated with success!')
      },
      error: (error: HttpErrorResponse): void => {
        this._isLoadingMainControls = false
        this.handlerResponseService.notifyError(error)
      },
    })
  }

  private saveUrlForm(): void {
    this._isLoadingUrlControls = true
    const dataCardGroupType: DataCardGroupType = this.service.getDataCardGroupTypeByType(
      this.dataCardType,
    )
    this.service
      .updateDeviationUrl(this.mainObject, this.getUrlFormValues(), dataCardGroupType)
      .subscribe({
        next: (resource: { data: any; type: DataCardGroupType }): void => {
          if (resource.type === 'FE' && resource.data) {
            this.notifyUrlDataCard.next({
              mainObject: this.mainObject,
              active: !!resource.data?.Object_Dev_Active,
              url: resource.data?.Object_Dev_Active,
            })
          }
          this._initialUrlForm = this.getUrlFormValues()
          this._isLoadingUrlControls = false
          this.handlerResponseService.notifySuccess(
            'Success',
            'Deviation URL updated with success!',
          )
        },
        error: (error: HttpErrorResponse): void => {
          this._isLoadingUrlControls = false
          this.handlerResponseService.notifyError(error)
        },
      })
  }

  public patchMainFormData(): void {
    if (this._mainFormData) {
      this.form.patchValue({
        condition: HelperService.toEmptyStringWhenBull(this._mainFormData?.Test_Condition),
        comment: HelperService.toEmptyStringWhenBull(this._mainFormData?.User_Comments),
        valve: HelperService.toEmptyStringWhenBull(this._mainFormData?.User_Sel_Code_Fe),
        sol1: HelperService.toEmptyStringWhenBull(this._mainFormData?.User_Sel_Code_SS1),
        sol2: HelperService.toEmptyStringWhenBull(this._mainFormData?.User_Sel_Code_SS2),
      })
    }

    this._initialMainForm = HelperService.deepCopy(this.getMainFormValues())
  }

  public patchFormUrlData(): void {
    if (this._urlFormData) {
      this.form.patchValue({
        url: this._urlFormData?.url !== null ? this._urlFormData?.url : '',
      })
    }

    this._initialUrlForm = HelperService.deepCopy(this.getUrlFormValues())
  }

  public getFuncLabel(typical: string): string {
    if (typical in TypicalFuncLabelEnum) {
      return TypicalFuncLabelEnum[typical]
    }
    return TypicalFuncLabelEnum.VLV
  }

  public getEqLabel(typical: string): string {
    if (typical in TypicalEquipmentLabelEnum) {
      return TypicalEquipmentLabelEnum[typical]
    }
    return TypicalEquipmentLabelEnum.VLV
  }

  public isInvalid(): boolean {
    return (
      this._isLoadingMainControls ||
      this._isLoadingUrlControls ||
      !!this._mainFormData?.Object_EquipmentNonOp ||
      this.form.invalid ||
      !this.formChanged()
    )
  }

  private formChanged(): boolean {
    return this.mainFormChanged() || this.urlFormChanged()
  }

  private mainFormChanged(): boolean {
    return JSON.stringify(this._initialMainForm) !== JSON.stringify(this.getMainFormValues())
  }

  private urlFormChanged(): boolean {
    return JSON.stringify(this._initialUrlForm) !== JSON.stringify(this.getUrlFormValues())
  }

  private getMainFormValues(): any {
    return HelperService.getObjectWithoutNullValues({
      condition: this.form.controls['condition'].value,
      comment: this.form.controls['comment'].value,
      valve: this.form.controls['valve'].value,
      sol1: this.form.controls['sol1'].value,
      sol2: this.form.controls['sol2'].value,
    })
  }

  private getUrlFormValues(): any {
    return HelperService.getObjectWithoutNullValues({
      url: this.form.controls['url'].value,
    })
  }

  ngOnInit(): void {
    this.initFormControls()
    this.initFormGroup()
  }
}
